import { apiFetch, type ChangedSinceResult } from './fetch';
import { apiFetchWithTimeout } from './fetch-timeout';

export type DocumentListItem = {
  id: string;
  name: string;
  protectionLevel?: number;
  recipientCount?: number;
  createdAt: string;
  hasFile?: boolean;
  originalContentType?: string;
  sourcePageCount?: number;
  showRecipientIdentity?: boolean;
  showVisibleWatermark?: boolean;
  activeGenerationStatus?: 'Pending' | 'Processing' | 'Succeeded' | 'Failed' | 'Cancelled' | null;
  generationProgressPercent?: number | null;
  isEnviosArchived?: boolean;
  canCancel?: boolean;
  primaryCounterpartyUserId?: string | null;
  /** Display name when sent-deliveries resolves a single recipient. */
  primaryRecipientName?: string | null;
  expiresAt?: string | null;
  isArchived?: boolean;
  /** Capture/print attempts in the last 7 days (owner list badge). */
  recentCaptureAlertCount?: number;
};

export type DashboardStatsDto = {
  totalDocuments?: number;
  totalRecipients?: number;
  totalDownloads?: number;
  leakChecks?: number;
  documentsTrend?: number[];
  recipientsTrend?: number[];
  downloadsTrend?: number[];
  leakChecksTrend?: number[];
};

export type PagedDocumentsResult = {
  items: DocumentListItem[];
  totalCount: number;
  page: number;
  pageSize: number;
};

export async function fetchDocuments(page = 1, pageSize = 50): Promise<DocumentListItem[]> {
  const pageResult = await fetchDocumentsPage(page, pageSize);
  return pageResult.items;
}

export async function fetchDocumentsPage(page = 1, pageSize = 50): Promise<PagedDocumentsResult> {
  const params = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
  });
  const res = await apiFetchWithTimeout(`/api/documents?${params}`);
  if (!res.ok) {
    throw new Error(`Failed to fetch documents (${res.status})`);
  }
  const data = await res.json();
  if (data && typeof data === 'object' && Array.isArray((data as PagedDocumentsResult).items)) {
    const paged = data as PagedDocumentsResult;
    return {
      items: paged.items,
      totalCount: paged.totalCount ?? paged.items.length,
      page: paged.page ?? page,
      pageSize: paged.pageSize ?? pageSize,
    };
  }
  const items = data as DocumentListItem[];
  return {
    items,
    totalCount: items.length,
    page,
    pageSize,
  };
}

/** Incremental sync: only documents that changed after `sinceIso`, plus a cursor for the next check. */
export async function fetchDocumentsChangedSince(
  sinceIso: string,
): Promise<ChangedSinceResult<DocumentListItem>> {
  const params = new URLSearchParams({ since: sinceIso });
  const res = await apiFetchWithTimeout(`/api/documents?${params}`);
  if (!res.ok) {
    throw new Error(`Failed to fetch changed documents (${res.status})`);
  }
  return (await res.json()) as ChangedSinceResult<DocumentListItem>;
}

export async function fetchSentDeliveries(
  page = 1,
  pageSize = 50,
  archived = false,
): Promise<DocumentListItem[]> {
  const params = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
    archived: String(archived),
  });
  const res = await apiFetchWithTimeout(`/api/documents/sent-deliveries?${params}`);

  if (!res.ok) {
    throw new Error(`sent deliveries failed (${res.status})`);
  }
  const data = await res.json();
  if (data && typeof data === 'object' && Array.isArray((data as PagedDocumentsResult).items)) {
    return (data as PagedDocumentsResult).items;
  }
  return data as DocumentListItem[];
}

export async function archiveEnviosDelivery(documentId: string): Promise<void> {
  const res = await apiFetch(`/api/documents/${documentId}/archive-envios`, { method: 'POST' });
  if (!res.ok) throw new Error('archive delivery failed');
}

export async function unarchiveEnviosDelivery(documentId: string): Promise<void> {
  const res = await apiFetch(`/api/documents/${documentId}/unarchive-envios`, { method: 'POST' });
  if (!res.ok) throw new Error('unarchive delivery failed');
}

export async function cancelSentDelivery(documentId: string): Promise<void> {
  const res = await apiFetch(`/api/documents/${documentId}/cancel-sent-delivery`, { method: 'POST' });
  if (!res.ok) throw new Error('cancel delivery failed');
}

export async function fetchDocumentStats(): Promise<DashboardStatsDto> {
  const res = await apiFetch('/api/documents/stats');
  if (!res.ok) {
    throw new Error(`Failed to fetch document stats (${res.status})`);
  }
  return (await res.json()) as DashboardStatsDto;
}

export async function fetchDocument<T = unknown>(documentId: string): Promise<T> {
  const res = await apiFetch(`/api/documents/${documentId}`);
  if (!res.ok) {
    throw new Error(`Failed to fetch document (${res.status})`);
  }
  return (await res.json()) as T;
}
